import React, { useState } from 'react';
import Modal from '../shared/Modal';
import { QuoteRecord } from '../../types';
import { WorkspaceBranding } from '../../lib/workspaces';
import { Mail, ArrowRight, Download, FileText, CheckCircle2, AlertTriangle, Clock, Paperclip, Loader, X } from 'lucide-react';
import { safeFetch, downloadPdf, triggerBlobDownload } from '../../lib/api';
import { buildApplicantPdfFileName } from '../../lib/pdfFileName';
import { auth } from '../../lib/firebase';
import { notifyToast } from '../../lib/toastStore';

interface QuotePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: QuoteRecord | null;
  branding?: WorkspaceBranding;
  onQuoteUpdated: () => void;
  onViewInvoice?: (invoiceId: string) => void;
}

export default function QuotePreviewModal({
  isOpen,
  onClose,
  quote,
  branding,
  onQuoteUpdated,
  onViewInvoice,
}: QuotePreviewModalProps) {
  const [converting, setConverting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  if (!isOpen || !quote) return null;

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const qId = quote.id || quote.quoteId;
      const blob = await downloadPdf(`/api/finance/quotes/${qId}/pdf`);
      triggerBlobDownload(blob, buildApplicantPdfFileName(`Quote-${quote.quoteNumber || qId}`, quote.applicantId || (quote as any).applicationId, quote.applicantName));
    } catch (err: any) {
      notifyToast.error(err.message || 'Error downloading PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleConvert = async (sendEmailDirectly: boolean = false) => {
    setConverting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch(`/api/admin/finance/quotes/${quote.id || quote.quoteId}/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sendEmail: sendEmailDirectly }),
      });

      if (res.success) {
        notifyToast('success', `Quote successfully converted to Invoice #${res.invoice?.invoiceNumber}`);
        onQuoteUpdated();
        onClose();
        if (onViewInvoice && res.invoice?.id) {
          onViewInvoice(res.invoice.id);
        }
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to convert quote to invoice');
    } finally {
      setConverting(false);
    }
  };

  const handleSendEmail = async () => {
    setEmailing(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch(`/api/admin/finance/quotes/${quote.id || quote.quoteId}/email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.success) {
        notifyToast('success', `Quote sent to ${quote.applicantEmail}`);
        onQuoteUpdated();
      }
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to email quote');
    } finally {
      setEmailing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Converted':
        return <span className="px-2.5 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded-full flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Converted</span>;
      case 'Accepted':
        return <span className="px-2.5 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Accepted</span>;
      case 'Sent':
        return <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Sent</span>;
      case 'Declined':
        return <span className="px-2.5 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Declined</span>;
      default:
        return <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-full flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Draft</span>;
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden text-slate-800 animate-fade-in">
        {/* Header Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white text-slate-900 shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center font-bold">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-900 font-display tracking-tight">
                Quote #{quote.quoteNumber}
              </h3>
              <p className="text-[11px] text-slate-500">Official estimate and pricing proposal</p>
            </div>
          </div>

          <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Top Actions Bar */}
          <div className="flex flex-wrap justify-between items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="flex items-center gap-2">
            {getStatusBadge(quote.status)}
            {quote.convertedInvoiceNumber && (
              <span className="text-xs text-slate-500 font-mono">
                Source for Invoice: <strong>#{quote.convertedInvoiceNumber}</strong>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              disabled={downloadingPdf}
              onClick={handleDownloadPdf}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 shadow-xs"
            >
              {downloadingPdf ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download PDF
            </button>

            <button
              type="button"
              disabled={emailing}
              onClick={handleSendEmail}
              className="px-3 py-1.5 bg-brand hover:bg-brand-active text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
            >
              {emailing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Email Quote
            </button>

            {quote.status !== 'Converted' && (
              <>
                <button
                  type="button"
                  disabled={converting}
                  onClick={() => handleConvert(false)}
                  className="px-3 py-1.5 bg-brand hover:bg-brand-600 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  {converting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Convert to Invoice
                </button>

                <button
                  type="button"
                  disabled={converting}
                  onClick={() => handleConvert(true)}
                  className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  {converting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Convert & Send
                </button>
              </>
            )}
          </div>
        </div>

        {/* Formatted Quote Document Sheet */}
        <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-2xs space-y-6 text-slate-900 text-xs">
          {/* Header */}
          <div className="flex justify-between items-start border-b border-slate-200 pb-4">
            <div>
              {branding?.loginLogoUrl || branding?.emailLogoUrl ? (
                <img src={branding.loginLogoUrl || branding.emailLogoUrl} alt="Logo" className="h-10 object-contain mb-2" />
              ) : (
                <h3 className="text-lg font-black text-slate-900">{branding?.companyName || 'Workspace Organization'}</h3>
              )}
              <p className="text-slate-300 font-medium">Official Quote / Estimate Document</p>
            </div>

            <div className="text-right space-y-1 font-mono">
              <h2 className="text-xl font-black text-brand">QUOTE #{quote.quoteNumber}</h2>
              <p className="text-slate-600">Issue Date: <strong>{quote.issueDate}</strong></p>
              <p className="text-slate-600">Expires: <strong>{quote.expirationDate}</strong></p>
            </div>
          </div>

          {/* Client & Metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <span className="text-[10px] uppercase font-extrabold text-slate-400 block">Client / Applicant</span>
              <span className="font-bold text-slate-900">{quote.applicantName}</span>
              <span className="block text-slate-500 truncate">{quote.applicantEmail}</span>
            </div>

            <div>
              <span className="text-[10px] uppercase font-extrabold text-slate-400 block">Program & Term</span>
              <span className="font-bold text-slate-900">{quote.programName || 'N/A'}</span>
              <span className="block text-slate-500">{quote.term || 'Standard Term'}</span>
            </div>

            <div>
              <span className="text-[10px] uppercase font-extrabold text-slate-400 block">Referrer</span>
              <span className="font-bold text-slate-900">{quote.referrerName || 'Direct Applicant'}</span>
            </div>

            <div>
              <span className="text-[10px] uppercase font-extrabold text-slate-400 block">Sponsor</span>
              <span className="font-bold text-slate-900">{quote.sponsorName || 'Unassigned'}</span>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-3">Line Item / Description</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-right">Rate</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {(quote.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="p-3">
                      <div className="font-bold text-slate-900">{item.name}</div>
                      {item.description && <div className="text-slate-500">{item.description}</div>}
                    </td>
                    <td className="p-3 text-center font-mono">{item.quantity}</td>
                    <td className="p-3 text-right font-mono">${Number(item.rate || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono font-bold text-slate-900">${Number(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1.5 text-xs font-medium">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono">${Number(quote.subtotal || 0).toFixed(2)}</span>
              </div>
              {Number(quote.totalDiscount || 0) > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount:</span>
                  <span className="font-mono">-${Number(quote.totalDiscount).toFixed(2)}</span>
                </div>
              )}
              {Number(quote.taxTotal || 0) > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax:</span>
                  <span className="font-mono">+${Number(quote.taxTotal).toFixed(2)}</span>
                </div>
              )}
              {Number(quote.adjustment || 0) !== 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Adjustment:</span>
                  <span className="font-mono">${Number(quote.adjustment).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-black text-slate-900 pt-2 border-t border-slate-300">
                <span>Total Amount:</span>
                <span className="font-mono text-brand">${Number(quote.total || 0).toFixed(2)} USD</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          {(quote.notes || quote.terms) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-slate-600">
              {quote.notes && (
                <div>
                  <h5 className="font-extrabold text-slate-800 uppercase tracking-wider text-[10px] mb-1">Notes</h5>
                  <p>{quote.notes}</p>
                </div>
              )}
              {quote.terms && (
                <div>
                  <h5 className="font-extrabold text-slate-800 uppercase tracking-wider text-[10px] mb-1">Terms & Conditions</h5>
                  <p>{quote.terms}</p>
                </div>
              )}
            </div>
          )}

          {/* Attachments List */}
          {quote.attachments && quote.attachments.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <h5 className="font-extrabold text-slate-800 uppercase tracking-wider text-[10px] flex items-center gap-1">
                <Paperclip className="w-3.5 h-3.5" /> Attached Files ({quote.attachments.length})
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {quote.attachments.map(att => (
                  <a
                    key={att.id}
                    href={att.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex justify-between items-center p-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-xs font-medium text-slate-800"
                  >
                    <span className="truncate flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-brand" /> {att.fileName}
                    </span>
                    <Download className="w-3.5 h-3.5 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
